import { useState } from 'react';

/* Channel avatar — the channel's real YouTube profile picture (ch.thumbnail).
   Falls back to a neutral initial badge when there's no artwork or the image
   fails to load — deliberately no decorative gradient/colour.
   When the channel lost authorization (ch.needs_reconnect / not connected),
   an amber "!" badge overlays the corner instead of hiding the avatar. */
export default function ChannelAvatar({ ch, size = 44, radius }) {
	const [failed, setFailed] = useState(false);
	if (!ch) return null;

	const br = radius ?? size * 0.28;
	const common = { width: size, height: size, borderRadius: br };
	const needsReconnect = ch.needs_reconnect || (ch.status && ch.status !== 'connected');

	let avatar;
	if (ch.thumbnail && !failed) {
		avatar = (
			<img
				src={ch.thumbnail}
				alt={ch.name || 'Channel'}
				referrerPolicy="no-referrer"
				loading="lazy"
				onError={() => setFailed(true)}
				className="flex-none object-cover bg-slate-100"
				style={common}
			/>
		);
	} else {
		const initial = (ch.name || '').trim().charAt(0).toUpperCase() || '?';
		avatar = (
			<div
				className="flex-none grid place-items-center bg-slate-100 text-slate-400 font-semibold"
				style={{ ...common, fontSize: Math.max(11, size * 0.42) }}
			>
				{initial}
			</div>
		);
	}

	if (!needsReconnect) return avatar;

	// Badge sits inside the avatar's corner (overlapping the artwork, not the
	// surrounding surface) so it renders cleanly on white cards and on the
	// gradient hero's ringed containers alike.
	const badge = Math.max(10, Math.round(size * 0.4));
	return (
		<span className="relative inline-flex align-top flex-none" style={{ width: size, height: size }} title="Channel needs to be reconnected">
			{avatar}
			<span
				className="absolute grid place-items-center rounded-full bg-amber-500 text-white font-bold shadow-[0_1px_3px_rgba(0,0,0,0.35)]"
				style={{
					width: badge,
					height: badge,
					right: -badge * 0.08,
					bottom: -badge * 0.08,
					fontSize: Math.max(7, badge * 0.62),
					lineHeight: 1,
				}}
			>
				!
			</span>
		</span>
	);
}
